import { useEffect, useState } from 'react';
import { X, FileText, Printer } from 'lucide-react';
import type { PedidoDetalle } from '@shared/types';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { logoUrl } from '@renderer/utils/logoUrl';
import { esWeb } from '@renderer/lib/entorno';
import { imprimirDocumento } from '@renderer/lib/impresionNavegador';
import { componentesEnRenglon, decirCantidadComponente, lineaComponente } from '@shared/componentesPaquete';
import { totalEnLetras } from '@shared/dineroEnLetras';
import { sinCobrar } from '@shared/cuentaMesa';

interface Props {
  abierto: boolean;
  pedidoId: number | null;
  pdfPath?: string | null;
  onClose: () => void;
  /**
   * Sprint 168 — Si el pedido no está cobrado, el botón principal es "Cobrar" (abre el cobro de ESTE
   * pedido). Las pantallas que no pueden cobrar no lo pasan y la ventana sólo avisa que falta.
   */
  onCobrar?: (pedidoId: number) => void;
}

const METODO_PAGO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo',
  tarjeta: 'Tarjeta',
  transferencia: 'Transferencia',
};

export function TicketPreviewModal({ abierto, pedidoId, pdfPath, onClose, onCobrar }: Props) {
  const [detalle, setDetalle] = useState<PedidoDetalle | null>(null);
  const [cargando, setCargando] = useState(false);
  const [imprimiendo, setImprimiendo] = useState(false);
  const negocio = useNegocioStore((s) => s.negocio);
  // Sprint 56.1 — En navegador no existe "abrir el PDF": `shell.openPath` es de
  // Electron y el archivo vive en el servidor. Aquí el botón imprime.
  const enWeb = esWeb();

  useEffect(() => {
    if (!abierto || !pedidoId) {
      setDetalle(null);
      return;
    }
    setCargando(true);
    window.api.pedidos.obtener(pedidoId).then((res) => {
      if (res.ok) setDetalle(res.data);
      setCargando(false);
    });
  }, [abierto, pedidoId]);

  if (!abierto) return null;

  const handleAbrirPdf = async () => {
    if (!pdfPath) return;
    const res = await window.api.ticket.abrirPdf(pdfPath);
    if (!res.ok) window.alertar({ titulo: 'No se pudo abrir', mensaje: res.error, variant: 'warn' });
  };

  /**
   * Sprint 111 — La impresora no puede secuestrar la venta.
   *
   * El dueño reportó que al tocar "Imprimir ticket" *"no pasa nada"*: si la térmica está apagada,
   * sin papel o desconectada, la promesa se queda esperando, el botón se queda en "Abriendo…" y la
   * pantalla no dice ni que falló. Un periférico caído tiene que fallar **ruidosamente y rápido**,
   * nunca en silencio y nunca para siempre: el ticket ya está guardado y se puede reimprimir.
   */
  const ESPERA_IMPRESORA_MS = 12000;

  const handleImprimirWeb = async () => {
    if (!pedidoId) return;
    setImprimiendo(true);
    const seRindio: { ok: false; error: string } = {
      ok: false,
      error:
        'La impresora no respondió. El ticket ya está guardado: puedes reimprimirlo desde Pedidos cuando la conectes.',
    };
    let temporizador: ReturnType<typeof setTimeout> | undefined;
    const res = await Promise.race([
      imprimirDocumento({ tipo: 'caja', pedidoId }),
      new Promise<typeof seRindio>((resolve) => {
        temporizador = setTimeout(() => resolve(seRindio), ESPERA_IMPRESORA_MS);
      }),
    ]);
    if (temporizador) clearTimeout(temporizador);
    setImprimiendo(false);
    if (!res.ok) {
      window.alertar({
        titulo: 'No se pudo imprimir',
        mensaje: res.error ?? 'Intenta de nuevo desde el menú de impresión del navegador.',
        variant: 'warn',
      });
    }
  };

  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';
  /**
   * Sprint 168 — ¿Esto es la cuenta o el recibo? El 28/10 esta ventana le dijo "Venta registrada" a
   * una mesa sin cobrar; la dueña imprimió, entregó y nunca cobró. Si falta cobrar, la ventana lo
   * dice en el título, en el papel y en el botón principal.
   */
  const esCuenta = detalle ? sinCobrar(detalle) : false;
  /**
   * Sprint 169 — "Se desperdicia mucho papel". Al cobrar, el ticket YA sale solo; esta ventana aparecía
   * después con un botón azul grande de "Imprimir ticket" y se tocaba por costumbre: 20 tickets COPIA
   * en una semana, casi todos a los pocos segundos del original. Si ya salió, lo principal es "Listo" y
   * la reimpresión queda como opción ("Otra copia"), para cuando el cliente la pide.
   */
  const yaSalio = !esCuenta && detalle?.ticketYaImpreso === true;
  const folioTitulo = detalle ? `Folio #${String(detalle.numeroPedido).padStart(5, '0')}` : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl max-h-[95vh] flex flex-col">
        {/*
          Sprint 111 — Encabezar con el hecho tranquilizador, no con el nombre del papel.

          El dueño reportó que llegar aquí se siente como que la venta **no ocurrió**: el título
          decía "Vista previa" y el otro botón decía "Cerrar", que en cualquier pantalla se lee
          como *cancelar*. Nadie se atreve a cerrar por miedo a perder el pedido. La venta ya está
          registrada y cobrada cuando este modal aparece; decirlo con el folio a la vista es todo
          lo que hacía falta.
        */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <FileText className={`h-5 w-5 ${esCuenta ? 'text-amber-700' : 'text-primary'}`} />
            <h2 className="font-semibold">
              {!detalle
                ? 'Vista previa del ticket'
                : esCuenta
                  ? `Cuenta · ${folioTitulo} · falta cobrar`
                  : `Venta registrada · ${folioTitulo}`}
            </h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto bg-slate-100 p-4">
          {cargando ? (
            <div className="text-center text-slate-400 py-8">Cargando...</div>
          ) : !detalle || !negocio ? (
            <div className="text-center text-slate-400 py-8">No se pudo cargar el ticket.</div>
          ) : (
            <TicketRender
              negocio={negocio}
              detalle={detalle}
              monedaSimbolo={monedaSimbolo}
              locale={locale}
              esCuenta={esCuenta}
            />
          )}
        </div>

        <footer className="border-t border-slate-200 bg-white px-5 py-3 flex flex-col gap-2">
          {/*
            Sprint 148 — Avisar ANTES de imprimir que este papel sale marcado como copia.

            La marca existe desde el Sprint 129 y es correcta: dos papeles iguales con el mismo
            folio son con lo que se cobra una venta dos veces. Pero verla aparecer sin haberla
            pedido asusta —"¿por qué dice COPIA, salió mal?"—, y quien la enseña al cliente
            necesita poder explicarla. Se dice aquí, con el motivo, antes de tocar el botón.

            Sólo se afirma cuando el servidor dice que ya hubo una emisión: si nunca se imprimió,
            el siguiente papel es el original y esta línea no aparece.
          */}
          {esCuenta && (
            <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-base text-amber-900" role="status">
              Esta cuenta <strong>todavía no está cobrada</strong>. Cuando te paguen, toca <strong>Cobrar</strong> y elige
              cómo te pagaron. Si no, el pedido se queda abierto.
            </p>
          )}
          {yaSalio && (
            <p className="rounded bg-emerald-50 px-3 py-2 text-base text-emerald-900" role="status">
              ✓ <strong>El ticket ya se mandó a la impresora.</strong> Si no salió o el cliente pide otro, toca{' '}
              <strong>Otra copia</strong> (saldrá marcada como COPIA).
            </p>
          )}
          <p className="text-xs text-slate-500 text-center">
            {enWeb
              ? 'El ticket se guardó como PDF. Puedes imprimirlo en tu impresora térmica.'
              : 'Sin impresora configurada. El ticket se ha guardado como PDF.'}
          </p>
          <div className="flex gap-2">
            {/*
              Sprint 111 — "Listo", no "Cerrar". La venta ya está hecha; este botón termina el
              trámite, no lo cancela. Y es `min-h-[44px]` como su compañero: son los dos botones
              que se tocan ciento cincuenta veces al día.
            */}
            <button
              onClick={onClose}
              className={`${yaSalio ? 'btn-primary order-last' : 'btn-ghost'} flex-1 min-h-[44px]`}
            >
              {esCuenta ? 'Ahora no' : 'Listo'}
            </button>
            {esCuenta && onCobrar && detalle && (
              <button
                onClick={() => onCobrar(detalle.id)}
                className="btn-primary flex-1 min-h-[44px] order-last"
              >
                Cobrar
              </button>
            )}
            {enWeb ? (
              <button
                onClick={handleImprimirWeb}
                disabled={!pedidoId || imprimiendo}
                className={`${(esCuenta && onCobrar) || yaSalio ? 'btn-secondary' : 'btn-primary'} flex-1 min-h-[44px]`}
              >
                <Printer className="h-4 w-4" />
                {imprimiendo ? 'Abriendo...' : esCuenta ? 'Imprimir la cuenta' : yaSalio ? 'Otra copia' : 'Imprimir ticket'}
              </button>
            ) : (
              <button
                onClick={handleAbrirPdf}
                disabled={!pdfPath}
                className="btn-primary flex-1"
              >
                <Printer className="h-4 w-4" />
                Abrir PDF para imprimir
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function TicketRender({
  negocio,
  detalle,
  monedaSimbolo,
  locale,
  esCuenta = false,
}: {
  negocio: NonNullable<ReturnType<typeof useNegocioStore.getState>['negocio']>;
  detalle: PedidoDetalle;
  monedaSimbolo: string;
  locale: string;
  esCuenta?: boolean;
}) {
  const folioPad = String(detalle.numeroPedido).padStart(5, '0');
  const fecha = new Date(detalle.fechaCreacion);
  const fechaStr = `${String(fecha.getDate()).padStart(2, '0')}/${String(fecha.getMonth() + 1).padStart(2, '0')}/${fecha.getFullYear()} ${String(fecha.getHours()).padStart(2, '0')}:${String(fecha.getMinutes()).padStart(2, '0')}`;

  return (
    <div className="mx-auto bg-white p-4 shadow-md font-mono text-[11px] leading-tight text-slate-900" style={{ width: '80mm', maxWidth: '100%' }}>
      {/* Sprint 168 — Igual que el papel: si falta cobrar, es la CUENTA. */}
      {esCuenta && (
        <div className="mb-2 text-center font-bold">
          <div className="text-lg">CUENTA</div>
          <div>NO ES COMPROBANTE DE PAGO</div>
        </div>
      )}
      {/* Logo */}
      {negocio.logoPath && (
        <div className="mb-2 flex justify-center">
          <img src={logoUrl(negocio.fechaModificacion ?? negocio.id)} alt="Logo" className="h-16 w-16 object-contain" />
        </div>
      )}

      {/* Header negocio */}
      <div className="text-center font-bold text-sm">{negocio.nombre}</div>
      {negocio.rfc && <div className="text-center">RFC: {negocio.rfc}</div>}
      {negocio.direccion && <div className="text-center text-[10px]">{negocio.direccion}</div>}
      {negocio.telefono && <div className="text-center text-[10px]">Tel: {negocio.telefono}</div>}

      <Divisor />

      {/* Folio + fecha */}
      <div>Folio: <span className="font-bold">{folioPad}</span></div>
      <div>Fecha: {fechaStr}</div>
      {detalle.tipo === 'delivery' && detalle.clienteNombre && (
        <>
          <div className="mt-1 border-t border-dashed border-slate-300 pt-1">
            <div>Cliente: {detalle.clienteNombre}</div>
          </div>
        </>
      )}

      <Divisor />

      {/*
        Sprint 148 — Sólo los renglones vivos, como el papel.

        Los tres formatos que salen de la impresora filtran `estado: 'activo'` desde siempre; esta
        vista previa no, y nadie lo había notado porque hasta el Sprint 145 no se podía cancelar un
        renglón de una venta cobrada. Desde que sí, al reimprimir la nota de una venta corregida
        aquí aparecían **los dos** renglones —el equivocado y el bueno— sumando más que el total
        de abajo. El cliente leería una nota que no cuadra consigo misma.

        Es el mismo principio que el Sprint 138 dejó escrito al revés: una vista previa que enseña
        algo distinto del papel no es una vista previa, es una segunda versión del ticket.
      */}
      {detalle.items
        .filter((it) => it.estado !== 'cancelado')
        .map((it) => (
          <div key={it.id} className="mb-1">
            <div className="flex justify-between">
              <span>{it.cantidad}× {it.productoNombre ?? it.paqueteNombre}</span>
              <span>{formatPrecio(it.subtotal, monedaSimbolo, locale)}</span>
            </div>
            {it.personalizacionJson && <PersonalizacionPreview json={it.personalizacionJson} />}
          {/*
            Sprint 138 — La previsualización enseña lo MISMO que va a salir de la impresora,
            con el mismo rotulado (`lineaComponente`). Una vista previa que muestra menos que el
            papel no es una vista previa: es una segunda versión del ticket que puede desfasarse.
          */}
            {(() => {
              const comps = (it.paqueteProductos ?? []).map((pp) => ({
                cantidad: pp.excluido ? '' : decirCantidadComponente(pp.cantidad, pp.unidad),
                nombre: pp.excluido ? pp.productoBaseNombre : pp.productoFinalNombre ?? pp.productoBaseNombre,
                esSustituto: pp.esSustituido,
                excluido: pp.excluido,
              }));
              // Sprint 169 — Igual que el papel: lo que trae el paquete, seguido.
              if (comps.length > 1) return <div className="pl-2 text-[11px]">{componentesEnRenglon(comps)}</div>;
              return comps.map((c, i) => (
                <div key={i} className="pl-2 text-[11px]">
                  {lineaComponente(c)}
                </div>
              ));
            })()}
            {it.notasEspeciales && (
              <div className="pl-2 text-[11px] font-semibold text-slate-800">
                Nota: {it.notasEspeciales}
              </div>
            )}
          </div>
        ))}

      <Divisor />

      {/* Totales */}
      <div className="flex justify-between">
        <span>Subtotal</span>
        <span>{formatPrecio(detalle.subtotal, monedaSimbolo, locale)}</span>
      </div>
      {detalle.impuesto > 0 && (
        <div className="flex justify-between">
          <span>Impuesto</span>
          <span>{formatPrecio(detalle.impuesto, monedaSimbolo, locale)}</span>
        </div>
      )}
      <div className="flex justify-between font-bold text-sm mt-1 pt-1 border-t border-slate-400">
        <span>TOTAL</span>
        <span>{formatPrecio(detalle.total, monedaSimbolo, locale)}</span>
      </div>
      {/*
        Sprint 153 — El total en letras, igual que en el papel.

        Va aquí por la misma razón del Sprint 148: una vista previa que enseña algo distinto de lo
        que sale de la impresora no es una vista previa, es una segunda versión del ticket. La
        frase la arma el MISMO módulo que usan los tres formatos.
      */}
      <div className="mt-1 text-[11px] font-bold leading-tight">
        {totalEnLetras(detalle.total)}
      </div>

      {/*
        Sprint 111 — El ticket decía siempre "Pago: —".

        Indexaba `METODO_PAGO_LABEL` con `detalle.estado` ('pagado'), y ese diccionario tiene por
        llaves los métodos ('efectivo', 'tarjeta', 'transferencia'). La búsqueda nunca acertaba, así
        que caía al guion. El método real vive en `pagos[]`, que además soporta el cobro mixto.
      */}
      {detalle.pagos.length > 0 && (
        <div className="mt-1 text-[10px]">
          {detalle.pagos.length === 1 ? (
            <>Pago: {METODO_PAGO_LABEL[detalle.pagos[0].metodo] ?? detalle.pagos[0].metodo}</>
          ) : (
            /*
              Sprint 145 — Con más de un pago, **cada uno con su monto**.

              Decía "Pago: Efectivo + Tarjeta" y ya. Servía para el cobro mixto de siempre —dos
              formas en el mismo momento—, pero desde que se puede cobrar una diferencia días
              después, esa línea es lo único que explica por qué el cliente pagó dos veces. Sin los
              montos, el papel no permite reconstruir nada: quien reclame tendrá razón.
            */
            <>
              <div>Pagos:</div>
              {detalle.pagos.map((p) => (
                <div key={p.id} className="flex justify-between">
                  <span>{METODO_PAGO_LABEL[p.metodo] ?? p.metodo}</span>
                  <span>{formatPrecio(p.monto, monedaSimbolo, locale)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/*
        Sprint 137 — Con cuánto pagó y cuánto se le devuelve.

        Encontrado recorriendo el sistema en el navegador: el cobro calculaba el cambio, la base
        lo guardaba y el papel lo imprimía… pero esta pantalla —la que queda después de cobrar, y
        la única que ve un negocio que todavía no tiene impresora— no lo decía. El cliente que
        pregunta "¿cuánto me toca de cambio?" mientras el cajero mira esta ventana no tenía
        respuesta a la vista.

        Se deriva de lo guardado, igual que el ticket impreso, para que no puedan desfasarse. Y
        en tamaño normal, no en 10px: es una cifra de dinero que alguien va a leer en voz alta.
      */}
      {detalle.pagos
        .filter((p) => p.metodo === 'efectivo' && p.recibido != null)
        .map((p) => (
          <div key={p.id} className="mt-1 border-t border-dashed border-slate-300 pt-1 text-xs">
            <div className="flex justify-between">
              <span>Recibí</span>
              <span className="font-mono">
                {formatPrecio(p.recibido as number, monedaSimbolo, locale)}
              </span>
            </div>
            <div className="flex justify-between font-bold">
              <span>Su cambio</span>
              <span className="font-mono">
                {formatPrecio(
                  (p.recibido as number) - p.monto - p.propina,
                  monedaSimbolo,
                  locale,
                )}
              </span>
            </div>
          </div>
        ))}

      {esCuenta && <div className="mt-2 text-center font-bold">*** PENDIENTE DE PAGO ***</div>}

      {/* Mensaje pie — Sprint 169: UNA despedida, la misma que imprime el papel. */}
      <Divisor />
      <div className="text-center text-[10px]">{negocio.mensajePieTicket || '¡Gracias por su visita!'}</div>
    </div>
  );
}

function Divisor() {
  return <div className="my-2 border-t border-dashed border-slate-400" />;
}

function PersonalizacionPreview({ json }: { json: string }) {
  try {
    const personalizacion = JSON.parse(json);
    const incluidos = (personalizacion.componentes ?? [])
      .filter((c: { incluido: boolean }) => c.incluido)
      .map((c: { nombre: string }) => c.nombre);
    if (incluidos.length === 0) return null;
    return <div className="text-[10px] text-slate-600 pl-2">+ {incluidos.join(', ')}</div>;
  } catch {
    return null;
  }
}
